import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '@/api/attendance';
import type { SyncAttendancePayload } from '@/types/attendance';

const K = {
  meetings: (groupId: string) => ['attendance', 'meetings', groupId] as const,
  summary: (groupId: string) => ['attendance', 'summary', groupId] as const,
  meeting: (id: string) => ['attendance', 'meeting', id] as const,
  memberHistory: (memberId: string, days: number) => ['attendance', 'member', memberId, days] as const,
  dropOff: (filters: unknown) => ['attendance', 'drop-off', filters] as const,
};

export function useGroupMeetings(groupId: string | undefined) {
  return useQuery({
    queryKey: K.meetings(groupId ?? ''),
    queryFn: () => api.listGroupMeetings(groupId!),
    enabled: !!groupId,
  });
}

export function useAttendanceSummary(groupId: string | undefined, limit: number = 8) {
  return useQuery({
    queryKey: [...K.summary(groupId ?? ''), limit],
    queryFn: () => api.getAttendanceSummary(groupId!, limit),
    enabled: !!groupId,
  });
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: K.meeting(id ?? ''),
    queryFn: () => api.getMeeting(id!),
    enabled: !!id,
  });
}

export function useResolveMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, date }: { groupId: string; date: string }) =>
      api.resolveMeeting(groupId, date),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: K.meetings(v.groupId) });
      void qc.invalidateQueries({ queryKey: K.summary(v.groupId) });
    },
  });
}

export function useSyncAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, payload }: { meetingId: string; payload: SyncAttendancePayload }) =>
      api.syncAttendance(meetingId, payload),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: K.meeting(data.id) });
      void qc.invalidateQueries({ queryKey: K.meetings(data.group_id) });
      void qc.invalidateQueries({ queryKey: K.summary(data.group_id) });
    },
  });
}

export function useCopyPrevious() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) => api.copyPreviousAttendance(meetingId),
    onSuccess: (res) => {
      const meeting = res.meeting.data;
      void qc.invalidateQueries({ queryKey: K.meeting(meeting.id) });
      void qc.invalidateQueries({ queryKey: K.meetings(meeting.group_id) });
    },
  });
}

export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMeeting(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

export function useMemberAttendanceHistory(memberId: string | undefined, days: number = 90) {
  return useQuery({
    queryKey: K.memberHistory(memberId ?? '', days),
    queryFn: () => api.getMemberAttendanceHistory(memberId!, days),
    enabled: !!memberId,
  });
}

export function useDropOffReport(params?: Parameters<typeof api.getDropOffReport>[0]) {
  return useQuery({
    queryKey: K.dropOff(params ?? {}),
    queryFn: () => api.getDropOffReport(params),
  });
}
